import Vue from 'vue';
import { CC_CONTAINER_TYPES } from '../../infrastructure/ioc/cc-container-types';
import { Component, Inject, Watch } from 'vue-property-decorator';
import CcServiceInterface from '../../service/cc-service-interface';
import CcProductstock from '@/main/webapp/resources/src/vue/domain/productstock/cc-productstock';
import { Utils } from '@/main/webapp/resources/src/vue/infrastructure/utils/helpers';
import CcImage from '@/main/webapp/resources/src/vue/domain/image/cc-image';
import carousel from 'vue-owl-carousel2';
import CcBannerArea from '@/main/webapp/resources/src/vue/domain/banner/cc-banner-area';
import CcOrder from '@/main/webapp/resources/src/vue/domain/order/cc-order';
import CcCustomer from '@/main/webapp/resources/src/vue/domain/user/cc-customer';
import CcUser from '@/main/webapp/resources/src/vue/domain/user/cc-user';
import CcCategory from '@/main/webapp/resources/src/vue/domain/category/cc-category';
const UiAccordionComponent = () => import(/* webpackChunkName: 'ui-accordion' */ '../ui-accordion/ui-accordion.vue');
const CcProductStockControllerComponent = () => import(/* webpackChunkName: 'cc-product-stock-controller' */ '../../components/cc-product-stock-controller/cc-product-stock-controller.vue');
const CcOrdersSummaryComponent = () => import(/* webpackChunkName: 'cc-order-summary' */ '../../components/cc-order-summary/cc-order-summary.vue');
const CcBannerAreaComponent = () => import(/* webpackChunkName: 'cc-banner-area' */ '../cc-banner-area/cc-banner-area.vue');

@Component({
	components: {
		UiAccordion: UiAccordionComponent,
		CcBannerArea: CcBannerAreaComponent,
		CcOrderSummary: CcOrdersSummaryComponent,
		CcProductStockController: CcProductStockControllerComponent,
		CcProductStockInformations: Utils.getCustomComponent('cc-product-stock-informations'),
		CcProductStock: Utils.getCustomComponent('cc-product-stock'),
		carousel
	},
	filters: {
		imageUrl: (productStock: CcProductstock, image: CcImage) => {
			return ((image != null) ? `${Utils.getPermalik('/products/')}${productStock.product.code}/images/${image.name}?width=${CcPageProductComponent.PREVIEW_WIDTH}&height=${CcPageProductComponent.PREVIEW_HEIGHT}` : null);
		},
		decimal: (value: string) => {
			return new Intl.NumberFormat(navigator.language, { style: 'decimal', minimumFractionDigits: 2 }).format(parseFloat((parseFloat(value) || 0).toFixed(2)));
		},
		price: (value: string) => {
			return new Intl.NumberFormat(navigator.language, { style: 'currency', currency: 'EUR' }).format(parseFloat(value) || 0);
		},
		unit: (value: string, simple: boolean) => {
			const prefix: string = simple ? '' : 'al ';
			return ((value != null) ? `${prefix}${value.toLowerCase()}` : '');
		}
	}
})
export default class CcPageProductComponent extends Vue {
	static readonly PREVIEW_WIDTH: string = '600';
	static readonly PREVIEW_HEIGHT: string = '600';

	@Inject(CC_CONTAINER_TYPES.CcServiceInterface)
	private service!: CcServiceInterface;

	private productAlias: string = '';
	private similarProductStocks: Array<CcProductstock> = [];
	private productStock: CcProductstock = null;
	private productStockReady: boolean = false;
	private carouselInitialized: boolean = false;
	private responsiveItems: number = 0;
	private selectedThumb: number = 0;
	private breadCrumbsPath: Array<CcCategory> = [];

	get order (): CcOrder {
		return this.$store.getters.order;
	}

	get description (): string {
		return this.productStock ? this.productStock.product.customerDescription ? this.productStock.product.customerDescription : '' : '';
	}

	get salesDescription (): string {
		return this.productStock ? this.productStock.product.salesDescription ? this.productStock.product.salesDescription : '' : '';
	}

	get contentQuantity (): number {
		return this.productStock ? this.productStock.product.contentQuantity ? this.productStock.product.contentQuantity : 0 : 0;
	}

	get contentUnit (): string {
		return this.productStock ? this.productStock.product.contentUnit ? this.productStock.product.contentUnit.toLowerCase() : '' : '';
	}

	get averageWeight (): string {
		let averageWeight:string = this.productStock ? this.productStock.product.averageWeight ? this.productStock.product.averageWeight : null : null;
		return (averageWeight) ? this.$options.filters.decimal(averageWeight) : '';
	}

	get unitLowerCase (): string {
		return this.productStock ? this.productStock.product.unit ? this.productStock.product.unit.toLowerCase() : '' : '';
	}

	get code (): string {
		return (this.productStock) ? this.productStock.product.code : '';
	}

	get brand (): string {
		return this.productStock.product.brands[0];
	}

	get unit (): string {
		return this.productStock.product.customerUnit ? this.productStock.product.customerUnit : this.productStock.product.unit;
	}

	get bannerAreaProducts (): CcBannerArea {
		return this.$store.getters.bannerAreaProducts;
	}

	get navText (): Array<string> {
		return [
			'<i class="ui-button-icon ui-material-icons-outlined">arrow_back_ios</i>',
			'<i class="ui-button-icon ui-material-icons-outlined">arrow_forward_ios</i>'
		];
	}

	get showFeaturesPanel () : boolean {
		return ((this.productStock.product.salesDescription !== '') || (this.productStock.product.features.length > 0) || (this.productStock.product.marketingDescription !== '') || (this.productStock.product.brands.length > 0));
	}

	get showIngredientsPanel () : boolean {
		return ((this.productStock.product.ingredients.length > 0) || (this.productStock.product.allergens.length > 0) || (this.productStock.product.additives.length > 0));
	}

	get showTextualNutritionPanel () : boolean {
		return ((this.productStock.product.textualNutrition !== null) || (this.productStock.product.numericNutrition !== null));
	}

	get showOtherInformationsPanel () : boolean {
		return (this.productStock.product.otherInformation !== '');
	}

	get showOptionAdvicesPanel () : boolean {
		return ((this.productStock.product.storage !== '') || (this.productStock.product.recyclingNotes.length > 0));
	}

	get firstInformationsOpenPanel () : string {
		if (this.showFeaturesPanel) {
			return 'features-panel';
		}
		if (this.showIngredientsPanel) {
			return 'ingredients-panel';
		}
		if (this.showTextualNutritionPanel) {
			return 'textual-nutrition-panel';
		}
		if (this.showOtherInformationsPanel) {
			return 'other-informations-panel';
		}
		if (this.showOptionAdvicesPanel) {
			return 'options-advices-panel';
		}
	}

	get similarProductStocksLength ():string {
		return new Intl.NumberFormat(navigator.language).format(this.similarProductStocks.length);
	}

	get secondaryCustomers (): Array<CcCustomer> {
		return this.$store.getters.secondaryCustomers;
	}

	get user (): CcUser {
		return this.$store.getters.user;
	}

	get selectedCategoryPath (): string {
		return this.$store.getters.searchCategoryPaths;
	}

	get breadcrumbs (): Array<any> {
		return this.breadCrumbsPath.map((category: CcCategory) => {
			return { label: category.name, path: category.path };
		});
	}

	get categories (): Array<CcCategory> {
		return this.$store.getters.categories;
	}

	set categories (categories: Array<CcCategory>) {
		this.$store.dispatch('setCategories', categories);
	}

	@Watch('$route')
	private changeRouteId (to: any, from: any) {
		if (from.params.productAlias !== to.params.productAlias) {
			this.initializeComponent();
		}
	}

	@Watch('user.currentCustomer')
	private currentCustomerChange (newCustomer: CcCustomer, oldCustomer: CcCustomer) {
		if (newCustomer?.code !== oldCustomer?.code) {
			this.initializeComponent();
		}
	}

	@Watch('selectedCategoryPath')
	private onChangeSelectedCategoryPath () {
		this.recursiveCategoryLoad();
	}

	@Watch('order')
	ordersChange (newOrder: any, oldOrder: any) {
		if (newOrder?.shippingInformation?.code !== oldOrder?.shippingInformation?.code) {
			this.initializeComponent();
		}
	}

	beforeMount () {
		this.loadCustomCategory();
	}

	mounted () {
		this.initializeComponent();
	}

	updated () {
		let items = 2;
		if (document.documentElement.offsetWidth >= 992) {
			items = 4;
		}
		if (document.documentElement.offsetWidth >= 1400) {
			items = 6;
		}
		this.responsiveItems = items;
		// this.responsiveItems = (document.documentElement.offsetWidth >= 992 ? 4 : 2);
		// this.carouselChangeButton();
	}

	private findCategory (searchCode: string, categoryList: Array<CcCategory>): CcCategory {
		return categoryList.find((category: CcCategory) => category.code === searchCode);
	}

	private carouselChange (event: any) {
		let newKey: number = event.item.index % event.item.count;
		this.selectThumb(newKey);
	}

	private selectThumb (key: number) {
		if (this.selectedThumb !== key) {
			this.selectedThumb = key;
		}
	}

	private initializeComponent () {
		this.productAlias = this.$route.params.productAlias;
		this.loadProductStock();
		this.loadSimilarProducts();
	}

	private loadSimilarProducts () {
		this.service.getSimilarProductStock(this.productAlias)
			.then((similarProductStocksData: Array<CcProductstock>) => {
				this.similarProductStocks = similarProductStocksData;
				this.productStockReady = this.similarProductStocks.length > 0;
			})
			.catch((error: Error) => {
				Utils.defaultApiErrorHandler(error);
			});
	}

	private loadProductStock () {
		this.service
			.getProductStock(this.productAlias)
			.then((productStockData: any) => {
				this.productStock = productStockData;

				const description: string = this.productStock.product.customerDescription ? this.productStock.product.customerDescription : this.productStock.product.description;
				document.title = description.concat(' | ').concat(Utils.getAppSubtitle());

				this.setAnaliticsTraker();
			})
			.catch((error: Error) => {
				Utils.defaultApiErrorHandler(error);
			});
	}

	private calculateRowValue (rowIndex:number, allValues: Array<any>, allHeader: Array<any>): Array<any> {
		const colsNumber: number = allHeader.length;
		const start: number = rowIndex * colsNumber;
		const end: number = start + colsNumber;
		const values: Array<any> = allValues.slice(start, end);
		const result: Array<any> = [];

		values.forEach(function (value: any, index: number) {
			result.push({ value: value, header: allHeader[index] });
		});

		return result;
	}

	private isNote (rowIndex:number, allValues: Array<any>, allHeader: Array<any>): boolean {
		const colsNumber: number = allHeader.length;
		const start: number = rowIndex * colsNumber;
		const end: number = start + colsNumber;
		const values: Array<any> = allValues.slice(start, end);
		let result: boolean = true;

		values.forEach(function (value: any) {
			result = result && (value === '');
		});

		return result;
	}

	private setAnaliticsTraker () {
		Utils.setAnaliticsTraker({
			event: 'click_product',
			item_name: this.description,
			item_id: this.code,
			price: this.productStock.price,
			store: this.order.store.name
		});
	}

	private recursiveCategoryLoad () {
		if (this.productStock) {
			const path: string = this.productStock.product.ecrCategory?.customCategory?.path || this.selectedCategoryPath || '';
			const selectedPathChunks: Array<string> = path.split('-');
			let categoryParent: CcCategory = null;
			let categoryList: Array<CcCategory> = this.categories;
			this.breadCrumbsPath = [];

			selectedPathChunks.every((pathChunk: string) => {
				const category: CcCategory = this.findCategory(pathChunk, categoryList);
				if (category) {
					categoryParent = category;
					categoryList = category.children;
					this.breadCrumbsPath.push(category);
				} else if (categoryParent) {
					this.loadCustomCategory(categoryParent);
					categoryParent = null;
				}
				return !!category;
			});
		}
	}

	private loadCustomCategory (category: CcCategory = null) {
		if (!category?.leaf) {
			this.service.getCustomCategory({ path: category?.path })
				.then((categoryList: Array<CcCategory>) => {
					if (category) {
						category.children = categoryList;
					} else {
						this.categories = categoryList;
					}
					this.recursiveCategoryLoad();
				})
				.catch((error: Error) => {
					Utils.defaultApiErrorHandler(error);
				});
		}
	}
}
